import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "../select";
import React, { useState, useEffect } from "react";
import { Sheet, SheetTrigger, SheetContent, SheetClose } from "../sheet";
import { Calendar } from "../calendar";
import { Button } from "../button";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import * as Icons from "../../ui/shared/Icons";
import { CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useAssistants } from "@/hooks/useAssistants";
import { useContactFilters } from "@/hooks/useContactFilters";
import { useTeam } from "@/hooks/useTeam";
import { Badge } from "../badge";
import { useAuth } from "@/contexts/AuthContext";
import {
  canFilterChatByOtherMembers,
  canSeeChatOwnershipTabUnassigned,
} from "@/lib/chatContactScope";
import { formatDayKey } from "@/lib/accountTimezone";
import { useTranslation } from "react-i18next";

/** Calendar pickers hand back a local calendar day, not an instant — keep it zone-free. */
const toDayKey = (date: Date) => format(date, "yyyy-MM-dd");

export interface ContactFilters {
  assistantId: string | null;
  attributionId: string | null;
  tags: string[];
  campaigns: string[];
  dateFrom: Date | null;
  dateTo: Date | null;
}

interface MoreFilterProps {
  currentFilters?: ContactFilters;
  onFilterChange?: (filters: ContactFilters) => void;
  onClearFilters?: () => void;
}

const MoreFilter: React.FC<MoreFilterProps> = ({
  currentFilters = {
    assistantId: null,
    attributionId: null,
    tags: [],
    campaigns: [],
    dateFrom: null,
    dateTo: null
  },
  onFilterChange,
  onClearFilters
}) => {
  const { t } = useTranslation("common");
  const { assistants, loading: loadingAssistants } = useAssistants();
  const { uniqueTags, uniqueCampaigns, loading: loadingFilters } = useContactFilters();
  const { members, loading: loadingMembers } = useTeam();
  const { user } = useAuth();
  const showOtherMembersInFilter = canFilterChatByOtherMembers(user);
  const showUnassignedInFilter = canSeeChatOwnershipTabUnassigned(user);

  const [localAssistantId, setLocalAssistantId] = useState<string>(currentFilters?.assistantId || '');
  const [localAttributionId, setLocalAttributionId] = useState<string>(currentFilters?.attributionId || '');
  const [localTag, setLocalTag] = useState<string>(currentFilters?.tags[0] || '');
  const [localCampaign, setLocalCampaign] = useState<string>(currentFilters?.campaigns[0] || '');
  const [localDateFrom, setLocalDateFrom] = useState<Date | undefined>(currentFilters?.dateFrom || undefined);
  const [localDateTo, setLocalDateTo] = useState<Date | undefined>(currentFilters?.dateTo || undefined);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (currentFilters) {
      setLocalAssistantId(currentFilters.assistantId || '');
      setLocalAttributionId(currentFilters.attributionId || '');
      setLocalTag(currentFilters.tags[0] || '');
      setLocalCampaign(currentFilters.campaigns[0] || '');
      setLocalDateFrom(currentFilters.dateFrom || undefined);
      setLocalDateTo(currentFilters.dateTo || undefined);
    }
  }, [currentFilters]);

  const handleApplyFilters = () => {
    if (!onFilterChange) {
      console.warn('⚠️ [MoreFilter] onFilterChange não está definido');
      setIsOpen(false);
      return;
    }

    const newFilters: ContactFilters = {
      assistantId: localAssistantId || null,
      attributionId: localAttributionId || null,
      tags: localTag ? [localTag] : [],
      campaigns: localCampaign ? [localCampaign] : [],
      dateFrom: localDateFrom || null,
      dateTo: localDateTo || null,
    };

    console.log('🔍 [MoreFilter] Aplicando filtros:', newFilters);
    onFilterChange(newFilters);
    setIsOpen(false);
  };

  const handleClearFilters = () => {
    setLocalAssistantId('');
    setLocalAttributionId('');
    setLocalTag('');
    setLocalCampaign('');
    setLocalDateFrom(undefined);
    setLocalDateTo(undefined);

    if (onClearFilters) {
      onClearFilters();
    }
    setIsOpen(false);
  };

  const activeFiltersCount = currentFilters ? [
    currentFilters.assistantId,
    currentFilters.attributionId,
    currentFilters.tags.length > 0,
    currentFilters.campaigns.length > 0,
    currentFilters.dateFrom,
    currentFilters.dateTo
  ].filter(Boolean).length : 0;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger>
        <div className="border h-12 px-4 border-border bg-white hover:bg-[#F1F5F9] transition-colors duration-200 rounded-[12px] flex justify-center items-center gap-2 relative shadow-sm dark:bg-card dark:hover:bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none">
          <p className="text-foreground font-medium text-[14px]">
            {t("shared.moreFilters")}
          </p>
          <Icons.filter className="w-4 h-4 text-muted-foreground" />
          {activeFiltersCount > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center !bg-[#2563EB] hover:!bg-[#2563EB] !text-white !border-0 text-xs">
              {activeFiltersCount}
            </Badge>
          )}
        </div>
      </SheetTrigger>
      <SheetContent className="bg-card p-4 flex flex-col gap-4 border-border">
        <div className="w-full gap-2 flex justify-center items-center">
          <p className="text-[16px] font-semibold text-foreground">
            {t("shared.moreFilters")}
          </p>
          <Icons.filter className="w-4 h-4 text-muted-foreground" />
        </div>

        {/* Assistente */}
        <div className="space-y-2">
          <label className="text-sm text-muted-foreground">{t("shared.assistant")}</label>
          <Select value={localAssistantId} onValueChange={setLocalAssistantId}>
            <SelectTrigger className="w-full !text-foreground">
              <SelectValue placeholder={loadingAssistants ? t("shared.loading") : t("shared.pickAssistant")} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__clear__">{t("shared.all")}</SelectItem>
                {assistants.map((assistant) => (
                  <SelectItem key={assistant.id} value={assistant.id}>
                    {assistant.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Atribuído para */}
        <div className="space-y-2">
          <label className="text-sm text-muted-foreground">{t("shared.assignedTo")}</label>
          <Select value={localAttributionId} onValueChange={setLocalAttributionId}>
            <SelectTrigger className="w-full !text-foreground">
              <SelectValue placeholder={loadingMembers ? t("shared.loading") : t("shared.pickOwner")} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__clear__">{t("shared.all")}</SelectItem>
                {showUnassignedInFilter && (
                  <SelectItem value="__unassigned__">{t("shared.unassigned")}</SelectItem>
                )}
                {members
                  .filter((member) => member.auth_user_id)
                  .filter((member) =>
                    showOtherMembersInFilter
                      ? true
                      : member.auth_user_id === user?.auth_user_id
                  )
                  .map((member) => (
                    <SelectItem key={member.auth_user_id} value={member.auth_user_id!}>
                      {member.name}
                    </SelectItem>
                  ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Tag */}
        <div className="space-y-2">
          <label className="text-sm text-muted-foreground">{t("shared.tag")}</label>
          <Select value={localTag} onValueChange={setLocalTag}>
            <SelectTrigger className="w-full !text-foreground">
              <SelectValue placeholder={loadingFilters ? t("shared.loading") : uniqueTags.length === 0 ? t("shared.noTagFound") : t("shared.pickTag")} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__clear__">{t("shared.allF")}</SelectItem>
                {uniqueTags.map((tag) => (
                  <SelectItem key={tag} value={tag}>
                    {tag}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Campanha */}
        <div className="space-y-2">
          <label className="text-sm text-muted-foreground">{t("shared.campaign")}</label>
          <Select value={localCampaign} onValueChange={setLocalCampaign}>
            <SelectTrigger className="w-full !text-foreground">
              <SelectValue placeholder={loadingFilters ? t("shared.loading") : uniqueCampaigns.length === 0 ? t("shared.noCampaignFound") : t("shared.pickCampaign")} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__clear__">{t("shared.allF")}</SelectItem>
                {uniqueCampaigns.map((campaign) => (
                  <SelectItem key={campaign} value={campaign}>
                    {campaign}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Data Inicial */}
        <div className="space-y-2">
          <label className="text-sm text-muted-foreground">{t("shared.startDate")}</label>
          <Popover>
            <PopoverTrigger
              className="!w-full !min-h-[56px] text-[14px] font-medium !text-foreground border border-border !bg-card"
              asChild
            >
              <Button
                variant={"outline"}
                className={cn(
                  "w-full flex justify-between text-left font-normal",
                  !localDateFrom && "text-muted-foreground"
                )}
              >
                {localDateFrom ? formatDayKey(toDayKey(localDateFrom), { dateStyle: 'long' }) : <span>{t("shared.pickDate")}</span>}
                <CalendarIcon />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={localDateFrom}
                onSelect={setLocalDateFrom}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Data Final */}
        <div className="space-y-2">
          <label className="text-sm text-muted-foreground">{t("shared.endDate")}</label>
          <Popover>
            <PopoverTrigger
              className="!w-full !min-h-[56px] text-[14px] font-medium !text-foreground border border-border !bg-card"
              asChild
            >
              <Button
                variant={"outline"}
                className={cn(
                  "w-full flex justify-between text-left font-normal",
                  !localDateTo && "text-muted-foreground"
                )}
              >
                {localDateTo ? formatDayKey(toDayKey(localDateTo), { dateStyle: 'long' }) : <span>{t("shared.pickDate")}</span>}
                <CalendarIcon />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={localDateTo}
                onSelect={setLocalDateTo}
                initialFocus
                disabled={(date) => localDateFrom ? date < localDateFrom : false}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Buttons */}
        <div className="w-full flex gap-2 mt-auto">
          <Button
            onClick={handleClearFilters}
            variant="outline"
            className="flex-1 border-border bg-card hover:bg-hover text-foreground"
          >
            <X className="w-4 h-4 mr-2" />
            {t("shared.clear")}
          </Button>
          <Button onClick={handleApplyFilters} className="flex-1">
            {t("shared.applyFilters")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MoreFilter;
